import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem } from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import {
  LayoutDashboard, Plane, Package, ShoppingCart, Users, Wallet, CreditCard,
  Truck, Receipt, BarChart3, Store, Settings, Trash2, ClipboardList,
  Plus, History,
} from "lucide-react";

const RECENT_KEY = "jastip_cmd_recent";

const ACTIONS = [
  { key: "a-order", label: "Buat Order", icon: ShoppingCart, to: "/orders?new=1", testId: "cmd-action-order" },
  { key: "a-product", label: "Tambah Produk", icon: Package, to: "/products?new=1", testId: "cmd-action-product" },
  { key: "a-payment", label: "Catat Pembayaran", icon: Wallet, to: "/payments?new=1", testId: "cmd-action-payment" },
  { key: "a-customer", label: "Tambah Customer", icon: Users, to: "/customers?new=1", testId: "cmd-action-customer" },
  { key: "a-expense", label: "Tambah Pengeluaran", icon: Receipt, to: "/expenses?new=1", testId: "cmd-action-expense" },
];

const NAVS = [
  { key: "n-dash", label: "Dashboard", icon: LayoutDashboard, to: "/" },
  { key: "n-trips", label: "Trip", icon: Plane, to: "/trips" },
  { key: "n-products", label: "Produk", icon: Package, to: "/products" },
  { key: "n-orders", label: "Order", icon: ShoppingCart, to: "/orders" },
  { key: "n-customers", label: "Pelanggan", icon: Users, to: "/customers" },
  { key: "n-payments", label: "Pembayaran", icon: Wallet, to: "/payments" },
  { key: "n-purchases", label: "Pembelian", icon: CreditCard, to: "/purchases" },
  { key: "n-shopping", label: "Daftar Belanja", icon: ClipboardList, to: "/shopping-list" },
  { key: "n-deliveries", label: "Pengiriman", icon: Truck, to: "/deliveries" },
  { key: "n-expenses", label: "Pengeluaran", icon: Receipt, to: "/expenses" },
  { key: "n-reports", label: "Laporan", icon: BarChart3, to: "/reports" },
  { key: "n-catalogue", label: "Katalog", icon: Store, to: "/catalogue" },
  { key: "n-settings", label: "Pengaturan", icon: Settings, to: "/settings" },
  { key: "n-recycle", label: "Kotak Sampah", icon: Trash2, to: "/recycle-bin" },
];

function getRecents() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
}

export function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { trips } = useApp();
  const [query, setQuery] = useState("");
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [recents, setRecents] = useState([]);
  const debounceRef = useRef();

  // Cmd/Ctrl + K toggles the palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (open) { setQuery(""); setRecords([]); setRecents(getRecents()); }
  }, [open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setRecords([]); setLoadingRecords(false); return; }
    setLoadingRecords(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const [pr, cr, or] = await Promise.all([
          api.get("/products", { params: { search: q } }).catch(() => ({ data: { items: [] } })),
          api.get("/customers", { params: { search: q } }).catch(() => ({ data: [] })),
          api.get("/orders", { params: { search: q } }).catch(() => ({ data: { items: [] } })),
        ]);
        const products = (pr.data.items || pr.data || []).slice(0, 5).map((p) => ({
          key: `p-${p.id}`, label: p.name, sub: "Produk", icon: Package, to: "/products",
        }));
        const customers = (cr.data || []).slice(0, 5).map((c) => ({
          key: `c-${c.id}`, label: c.name, sub: "Customer", icon: Users, to: `/customers/${c.id}`,
        }));
        const orders = (or.data.items || or.data || []).slice(0, 5).map((o) => ({
          key: `o-${o.id}`, label: o.order_number, sub: o.customer_name || "Order", icon: ShoppingCart, to: "/orders",
        }));
        const tripHits = (trips || []).filter((t) => (t.name || "").toLowerCase().includes(q.toLowerCase())).slice(0, 4).map((t) => ({
          key: `t-${t.id}`, label: t.name, sub: "Trip", icon: Plane, to: "/trips",
        }));
        setRecords([...customers, ...products, ...orders, ...tripHits]);
      } finally { setLoadingRecords(false); }
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, trips]);

  const run = useCallback((item) => {
    try {
      const next = [{ label: item.label, to: item.to, sub: item.sub || null },
        ...getRecents().filter((r) => !(r.to === item.to && r.label === item.label))].slice(0, 8);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch { /* ignore */ }
    onOpenChange(false);
    navigate(item.to);
  }, [navigate, onOpenChange]);

  const q = query.trim().toLowerCase();
  const actionsF = q ? ACTIONS.filter((a) => a.label.toLowerCase().includes(q)) : ACTIONS;
  const navsF = q ? NAVS.filter((n) => n.label.toLowerCase().includes(q)) : NAVS;
  const showRecents = !q && recents.length > 0;
  const nothing = q.length >= 2 && !loadingRecords && actionsF.length === 0 && navsF.length === 0 && records.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 overflow-hidden max-w-[92vw] sm:max-w-[640px]" data-testid="command-palette-dialog">
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
          <CommandInput placeholder="Cari halaman, buat data, atau cari record…" value={query} onValueChange={setQuery} data-testid="command-palette-input" />
          <CommandList className="max-h-[60vh]">
            {showRecents && (
              <CommandGroup heading="Terakhir">
                {recents.map((r, i) => (
                  <CommandItem key={`r-${i}`} value={`recent-${i}-${r.label}`} onSelect={() => run(r)} data-testid="command-palette-result-item">
                    <History className="h-4 w-4 mr-2 opacity-60" />
                    <span className="flex-1 truncate">{r.label}</span>
                    {r.sub && <span className="text-xs text-muted-foreground font-mono">{r.sub}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {actionsF.length > 0 && (
              <CommandGroup heading="Aksi Cepat">
                {actionsF.map((a) => (
                  <CommandItem key={a.key} value={`action-${a.label}`} onSelect={() => run(a)} data-testid={a.testId}>
                    <a.icon className="h-4 w-4 mr-2 text-primary" />
                    <span className="flex-1 truncate">{a.label}</span>
                    <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {navsF.length > 0 && (
              <CommandGroup heading="Navigasi">
                {navsF.map((n) => (
                  <CommandItem key={n.key} value={`nav-${n.label}`} onSelect={() => run(n)} data-testid="command-palette-result-item">
                    <n.icon className="h-4 w-4 mr-2 opacity-70" />
                    <span className="flex-1 truncate">{n.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {q.length >= 2 && (
              <CommandGroup heading="Cari Record">
                {loadingRecords ? (
                  <div className="px-2 py-1.5 space-y-2" data-testid="skeleton-command-palette">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Skeleton className="h-6 w-6 rounded-full" />
                        <Skeleton className="h-3.5 flex-1" />
                        <Skeleton className="h-3 w-12" />
                      </div>
                    ))}
                  </div>
                ) : records.map((r) => (
                  <CommandItem key={r.key} value={`rec-${r.key}`} onSelect={() => run(r)} data-testid="command-palette-result-item">
                    <r.icon className="h-4 w-4 mr-2 opacity-70" />
                    <span className="flex-1 truncate">{r.label}</span>
                    <span className="text-xs text-muted-foreground font-mono">{r.sub}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {nothing && (
              <div className="py-10 text-center text-sm text-muted-foreground">Tidak ada hasil. Coba kata kunci lain.</div>
            )}
          </CommandList>
          <div className="flex items-center gap-3 border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
            <span className="font-mono">↑↓</span> navigasi
            <span className="font-mono">↵</span> pilih
            <span className="font-mono">esc</span> tutup
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
